import { type FormEvent, useCallback, useEffect, useState } from "react";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;

export interface MerchantLocation {
  id: string;
  slug: string;
  internalName: string;
  status: "draft" | "active" | "suspended" | "closed";
  displayName: string;
  timezone: string;
  address: {
    line1: string;
    line2: string | null;
    locality: string;
    region: string | null;
    postalCode: string;
    countryCode: string;
  };
  coordinate: {
    latitude: number;
    longitude: number;
  };
  joinUrl: string;
  publicReady: boolean;
}

export interface LocationDraft {
  slug: string;
  internalName: string;
  timezone: string;
  addressLine1: string;
  addressLine2: string;
  locality: string;
  region: string;
  postalCode: string;
  countryCode: string;
  latitude: string;
  longitude: string;
  displayNameEn: string;
  displayNameZhHant: string;
}

interface LocationInput {
  slug: string;
  internalName: string;
  timezone: string;
  addressLine1: string;
  addressLine2: string | null;
  locality: string;
  region: string | null;
  postalCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  localizations: {
    en: { displayName: string };
    "zh-Hant"?: { displayName: string };
  };
}

type LocationsState =
  | { status: "loading" }
  | { status: "error"; permission: boolean }
  | { status: "ready"; locations: MerchantLocation[] };

type SaveState =
  | { status: "idle" }
  | { status: "saving"; input: LocationInput; idempotencyKey: string }
  | {
      status: "error";
      input: LocationInput | null;
      idempotencyKey: string | null;
      conflict: boolean;
    }
  | { status: "complete" };

const emptyDraft: LocationDraft = {
  slug: "",
  internalName: "",
  timezone: "Europe/Berlin",
  addressLine1: "",
  addressLine2: "",
  locality: "",
  region: "",
  postalCode: "",
  countryCode: "DE",
  latitude: "",
  longitude: "",
  displayNameEn: "",
  displayNameZhHant: "",
};

export function normalizeLocationDraft(
  draft: LocationDraft,
): LocationInput | null {
  const latitude = Number(draft.latitude);
  const longitude = Number(draft.longitude);
  const slug = draft.slug.trim().toLowerCase();
  const englishName = draft.displayNameEn.trim();
  const traditionalChineseName = draft.displayNameZhHant.trim();
  if (
    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ||
    draft.internalName.trim().length === 0 ||
    draft.timezone.trim().length === 0 ||
    draft.addressLine1.trim().length < 2 ||
    draft.locality.trim().length === 0 ||
    draft.postalCode.trim().length < 2 ||
    !/^[A-Za-z]{2}$/.test(draft.countryCode.trim()) ||
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90 ||
    !Number.isFinite(longitude) ||
    longitude < -180 ||
    longitude > 180 ||
    englishName.length === 0
  ) {
    return null;
  }
  return {
    slug,
    internalName: draft.internalName.trim(),
    timezone: draft.timezone.trim(),
    addressLine1: draft.addressLine1.trim(),
    addressLine2: draft.addressLine2.trim() || null,
    locality: draft.locality.trim(),
    region: draft.region.trim() || null,
    postalCode: draft.postalCode.trim(),
    countryCode: draft.countryCode.trim().toUpperCase(),
    latitude,
    longitude,
    localizations: {
      en: { displayName: englishName },
      ...(traditionalChineseName
        ? { "zh-Hant": { displayName: traditionalChineseName } }
        : {}),
    },
  };
}

function isLocations(value: unknown): value is { items: MerchantLocation[] } {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Array.isArray((value as Record<string, unknown>).items)
  );
}

async function problemCode(response: Response): Promise<string> {
  const value: unknown = await response.json().catch(() => null);
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value as Record<string, unknown>).error &&
    typeof (value as Record<string, unknown>).error === "object"
  ) {
    const code = (
      (value as Record<string, unknown>).error as Record<string, unknown>
    ).code;
    if (typeof code === "string") return code;
  }
  return "UNEXPECTED_RESPONSE";
}

export function Locations({
  merchantId,
  canManage,
  locale,
  copy,
  initialLocations,
  onActiveCount,
  onCustomizeQr,
}: {
  merchantId: string;
  canManage: boolean;
  locale: Locale;
  copy: Copy;
  initialLocations?: MerchantLocation[];
  onActiveCount?: (count: number) => void;
  onCustomizeQr?: () => void;
}) {
  const [state, setState] = useState<LocationsState>(() =>
    initialLocations
      ? { status: "ready", locations: initialLocations }
      : { status: "loading" },
  );
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<LocationDraft>(emptyDraft);
  const [save, setSave] = useState<SaveState>({ status: "idle" });
  const [qrError, setQrError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await fetch("/v1/merchant/locations", {
        credentials: "include",
        headers: {
          "accept-language": locale,
          "x-merchant-id": merchantId,
        },
      });
      if (!response.ok) {
        setState({
          status: "error",
          permission: response.status === 401 || response.status === 403,
        });
        return;
      }
      const value: unknown = await response.json();
      if (isLocations(value)) {
        onActiveCount?.(
          value.items.filter((location) => location.status === "active").length,
        );
      }
      setState(
        isLocations(value)
          ? { status: "ready", locations: value.items }
          : { status: "error", permission: false },
      );
    } catch {
      setState({ status: "error", permission: false });
    }
  }, [locale, merchantId, onActiveCount]);

  useEffect(() => {
    if (!initialLocations) void load();
  }, [initialLocations, load]);

  const saveLocation = useCallback(
    async (input: LocationInput, idempotencyKey: string) => {
      setSave({ status: "saving", input, idempotencyKey });
      try {
        const response = await fetch("/v1/merchant/locations", {
          method: "POST",
          credentials: "include",
          headers: {
            "accept-language": locale,
            "content-type": "application/json",
            "idempotency-key": idempotencyKey,
            "x-merchant-id": merchantId,
          },
          body: JSON.stringify(input),
        });
        if (!response.ok) {
          const code = await problemCode(response);
          setSave({
            status: "error",
            input,
            idempotencyKey,
            conflict: response.status === 409 || code === "LOCATION_CONFLICT",
          });
          return;
        }
        setSave({ status: "complete" });
        setAdding(false);
        setDraft(emptyDraft);
        if (!initialLocations) await load();
      } catch {
        setSave({
          status: "error",
          input,
          idempotencyKey,
          conflict: false,
        });
      }
    },
    [initialLocations, load, locale, merchantId],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const input = normalizeLocationDraft(draft);
    if (!input) {
      setSave({
        status: "error",
        input: null,
        idempotencyKey: null,
        conflict: false,
      });
      return;
    }
    void saveLocation(
      input,
      `idem_location_ui_${crypto.randomUUID().replaceAll("-", "")}`,
    );
  };

  const downloadQr = async (location: MerchantLocation) => {
    setQrError(null);
    try {
      const response = await fetch(
        `/v1/merchant/locations/${encodeURIComponent(location.id)}/qr.svg`,
        {
          credentials: "include",
          headers: { "x-merchant-id": merchantId },
        },
      );
      if (!response.ok) throw new Error("qr request failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${location.slug}-collectie-join.svg`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      setQrError(location.id);
    }
  };

  if (state.status === "loading") {
    return (
      <section className="panel operations-status" aria-busy="true">
        <div className="loading-mark" aria-hidden="true" />
        <h2>{copy("merchant.locations.loading")}</h2>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section className="panel empty-state" role="alert">
        <span className="empty-mark" aria-hidden="true" />
        <h2>
          {copy(
            state.permission
              ? "merchant.locations.permissionTitle"
              : "merchant.locations.errorTitle",
          )}
        </h2>
        <p>
          {copy(
            state.permission
              ? "merchant.locations.permissionBody"
              : "merchant.locations.errorBody",
          )}
        </p>
        {!state.permission && (
          <button
            className="primary-button"
            type="button"
            onClick={() => void load()}
          >
            {copy("common.retry")}
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="locations-workspace">
      <div className="section-heading locations-heading">
        <div>
          <p className="eyebrow">{copy("merchant.locations.eyebrow")}</p>
          <h2>{copy("merchant.locations.title")}</h2>
          <p>{copy("merchant.locations.body")}</p>
        </div>
        {canManage && (
          <button
            className="primary-button compact"
            type="button"
            onClick={() => {
              setAdding((current) => !current);
              setSave({ status: "idle" });
            }}
          >
            {copy("merchant.locations.add")}
          </button>
        )}
      </div>
      {save.status === "complete" && (
        <p className="activity-feedback" role="status">
          {copy("merchant.locations.created")}
        </p>
      )}
      {adding && (
        <form className="panel location-form" onSubmit={submit}>
          <div className="section-heading">
            <div>
              <p className="eyebrow">{copy("merchant.locations.newEyebrow")}</p>
              <h2>{copy("merchant.locations.newTitle")}</h2>
            </div>
          </div>
          <div className="location-form-grid">
            <LocationField
              id="location-internal-name"
              label={copy("merchant.locations.internalName")}
              value={draft.internalName}
              onChange={(value) =>
                setDraft((current) => ({ ...current, internalName: value }))
              }
            />
            <LocationField
              id="location-slug"
              label={copy("merchant.locations.slug")}
              value={draft.slug}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  slug: value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, "")
                    .slice(0, 100),
                }))
              }
            />
            <LocationField
              id="location-name-en"
              label={copy("merchant.locations.displayNameEn")}
              value={draft.displayNameEn}
              onChange={(value) =>
                setDraft((current) => ({ ...current, displayNameEn: value }))
              }
            />
            <LocationField
              id="location-name-zh"
              label={copy("merchant.locations.displayNameZhHant")}
              value={draft.displayNameZhHant}
              required={false}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  displayNameZhHant: value,
                }))
              }
            />
            <LocationField
              id="location-address"
              label={copy("merchant.locations.addressLine1")}
              value={draft.addressLine1}
              onChange={(value) =>
                setDraft((current) => ({ ...current, addressLine1: value }))
              }
            />
            <LocationField
              id="location-address-2"
              label={copy("merchant.locations.addressLine2")}
              value={draft.addressLine2}
              required={false}
              onChange={(value) =>
                setDraft((current) => ({ ...current, addressLine2: value }))
              }
            />
            <LocationField
              id="location-locality"
              label={copy("merchant.locations.locality")}
              value={draft.locality}
              onChange={(value) =>
                setDraft((current) => ({ ...current, locality: value }))
              }
            />
            <LocationField
              id="location-region"
              label={copy("merchant.locations.region")}
              value={draft.region}
              required={false}
              onChange={(value) =>
                setDraft((current) => ({ ...current, region: value }))
              }
            />
            <LocationField
              id="location-postal"
              label={copy("merchant.locations.postalCode")}
              value={draft.postalCode}
              onChange={(value) =>
                setDraft((current) => ({ ...current, postalCode: value }))
              }
            />
            <LocationField
              id="location-country"
              label={copy("merchant.locations.countryCode")}
              value={draft.countryCode}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  countryCode: value.toUpperCase().slice(0, 2),
                }))
              }
            />
            <LocationField
              id="location-timezone"
              label={copy("merchant.locations.timezone")}
              value={draft.timezone}
              onChange={(value) =>
                setDraft((current) => ({ ...current, timezone: value }))
              }
            />
            <LocationField
              id="location-latitude"
              label={copy("merchant.locations.latitude")}
              value={draft.latitude}
              inputMode="decimal"
              onChange={(value) =>
                setDraft((current) => ({ ...current, latitude: value }))
              }
            />
            <LocationField
              id="location-longitude"
              label={copy("merchant.locations.longitude")}
              value={draft.longitude}
              inputMode="decimal"
              onChange={(value) =>
                setDraft((current) => ({ ...current, longitude: value }))
              }
            />
          </div>
          {save.status === "error" && (
            <p className="form-error" role="alert">
              {copy(
                save.conflict
                  ? "merchant.locations.conflict"
                  : save.input
                    ? "merchant.locations.saveError"
                    : "merchant.locations.validation",
              )}
            </p>
          )}
          <p className="field-help">
            {copy("merchant.locations.coordinateHelp")}
          </p>
          <div className="scanner-actions">
            <button
              className="quiet-button"
              type="button"
              onClick={() => setAdding(false)}
            >
              {copy("common.cancel")}
            </button>
            {save.status === "error" &&
            save.input &&
            save.idempotencyKey &&
            !save.conflict ? (
              <button
                className="primary-button"
                type="button"
                onClick={() =>
                  void saveLocation(save.input!, save.idempotencyKey!)
                }
              >
                {copy("merchant.locations.retrySame")}
              </button>
            ) : (
              <button
                className="primary-button"
                type="submit"
                disabled={save.status === "saving"}
              >
                {copy(
                  save.status === "saving"
                    ? "merchant.locations.saving"
                    : "merchant.locations.save",
                )}
              </button>
            )}
          </div>
        </form>
      )}
      {state.locations.length === 0 ? (
        <div className="panel empty-state">
          <span className="empty-mark" aria-hidden="true" />
          <h2>{copy("merchant.locations.emptyTitle")}</h2>
          <p>{copy("merchant.locations.emptyBody")}</p>
        </div>
      ) : (
        <div className="location-list">
          {state.locations.map((location) => (
            <article className="panel location-card" key={location.id}>
              <div className="location-card-copy">
                <div className="activity-title-line">
                  <h3>{location.displayName}</h3>
                  <span>
                    {copy(`merchant.locations.status.${location.status}`)}
                  </span>
                </div>
                <p>
                  {location.address.line1}, {location.address.postalCode}{" "}
                  {location.address.locality}
                </p>
                <dl className="location-facts">
                  <div>
                    <dt>{copy("merchant.locations.timezone")}</dt>
                    <dd>{location.timezone}</dd>
                  </div>
                  <div>
                    <dt>{copy("merchant.locations.coordinate")}</dt>
                    <dd>
                      {location.coordinate.latitude.toFixed(5)},{" "}
                      {location.coordinate.longitude.toFixed(5)}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="location-qr">
                <div className="location-qr-heading">
                  <div>
                    <p className="eyebrow">
                      {copy("merchant.locations.qrTitle")}
                    </p>
                    <strong
                      className={location.publicReady ? "ready" : "pending"}
                    >
                      {copy(
                        location.publicReady
                          ? "merchant.locations.publicReady"
                          : "merchant.locations.publicPending",
                      )}
                    </strong>
                  </div>
                  {canManage && onCustomizeQr && (
                    <button
                      className="text-button"
                      type="button"
                      onClick={onCustomizeQr}
                    >
                      {copy("merchant.locations.customizeQr")}
                    </button>
                  )}
                </div>
                <LocationQrPreview
                  locationId={location.id}
                  merchantId={merchantId}
                  alt={copy("merchant.locations.qrPreviewAlt", {
                    name: location.displayName,
                  })}
                  loadingCopy={copy("merchant.locations.qrPreviewLoading")}
                />
                <p>{copy("merchant.locations.qrSafety")}</p>
                <div className="location-qr-actions">
                  <button
                    className="primary-button compact"
                    type="button"
                    onClick={() => void downloadQr(location)}
                  >
                    {copy("merchant.locations.downloadQr")}
                  </button>
                </div>
                {qrError === location.id && (
                  <p className="form-error" role="alert">
                    {copy("merchant.locations.qrError")}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function LocationQrPreview({
  locationId,
  merchantId,
  alt,
  loadingCopy,
}: {
  locationId: string;
  merchantId: string;
  alt: string;
  loadingCopy: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/v1/merchant/locations/${encodeURIComponent(locationId)}/qr.svg`, {
      credentials: "include",
      headers: { "x-merchant-id": merchantId },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("qr unavailable");
        return response.blob();
      })
      .then((blob) => setUrl(URL.createObjectURL(blob)))
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setFailed(true);
        }
      });
    return () => controller.abort();
  }, [locationId, merchantId]);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  if (failed) {
    return (
      <div className="location-qr-preview failed" role="img" aria-label={alt} />
    );
  }
  return (
    <div className="location-qr-preview" aria-busy={!url}>
      {url ? <img src={url} alt={alt} /> : <span>{loadingCopy}</span>}
    </div>
  );
}

function LocationField({
  id,
  label,
  value,
  onChange,
  required = true,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  inputMode?: "decimal";
}) {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value}
        required={required}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
